import { useMemo } from "react";

import { useResponsive } from "../../../../../hooks/useResponsive";
import { formatCurrency, formatPercent } from "../../../../../utils";
import { BREAKPOINTS } from "../../../../../utils/constants";

import { SortableColumnHeader } from "../../../../../components/SortableColumnHeader";
import { buildLiabilitiesHoldings, sortLiabilities } from "./holdings";
import {
  BodyRow,
  Card,
  CardAmounts,
  CardFooter,
  CardList,
  CardPaidOff,
  CardProgressHeader,
  CardTop,
  CardValue,
  Cell,
  Dot,
  EndsText,
  HeaderCell,
  HeaderRow,
  MobileSummary,
  MutedText,
  NameText,
  ProgressFill,
  ProgressPercent,
  ProgressTrack,
  ProgressWrap,
  RateChip,
  RateText,
  Section,
  SummaryFooter,
  Table,
  TableCard,
  TableScroll,
  TypeLabel,
  TypeRow,
  ValueText,
} from "./styles";
import { LiabilitiesHoldingsProps, LiabilityRow, SortKey } from "./types";

const NUMERIC_COLUMNS: { key: SortKey; label: string }[] = [
  { key: "debt", label: "Debt" },
  { key: "paid", label: "Paid" },
];

const formatEnds = (year: number | null): string => {
  if (year === null) {
    return "—";
  }
  return year.toString();
};

export const LiabilitiesHoldings: React.FC<LiabilitiesHoldingsProps> = ({
  liabilities,
  onSelect,
  sort,
  onSortChange,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const baseRows = useMemo(
    () => buildLiabilitiesHoldings(liabilities),
    [liabilities],
  );
  const rows = useMemo(() => sortLiabilities(baseRows, sort), [baseRows, sort]);

  const totalDebt = useMemo(
    () => rows.reduce((sum, row) => sum + row.debt, 0),
    [rows],
  );

  const countLabel = `${rows.length} ${rows.length === 1 ? "liability" : "liabilities"}`;
  const summaryLabel = `${countLabel} · ${formatCurrency(totalDebt)} owed`;

  const handleSort = (key: SortKey) => {
    if (sort.key === key) {
      onSortChange({ key, dir: sort.dir === "asc" ? "desc" : "asc" });
      return;
    }
    onSortChange({ key, dir: "desc" });
  };

  if (isCompact) {
    return (
      <Section>
        <CardList>
          {rows.map((row) => (
            <Card key={row.id} onClick={() => onSelect(row.id)}>
              <CardTop>
                <NameText>{row.name}</NameText>
                <RateChip>{formatPercent(row.rate, 3)}</RateChip>
              </CardTop>
              <TypeRow>
                <Dot $color={row.typeColor} />
                <TypeLabel>{row.typeLabel}</TypeLabel>
              </TypeRow>
              <CardValue>{formatCurrency(row.debt)}</CardValue>
              <CardProgressHeader>
                <CardPaidOff>
                  {formatPercent(row.progress, 1)} paid off
                </CardPaidOff>
                <CardAmounts>
                  {formatCurrency(row.paid)} of {formatCurrency(row.lent)}
                </CardAmounts>
              </CardProgressHeader>
              <ProgressTrack>
                <ProgressFill $width={Math.min(row.progress, 100)} />
              </ProgressTrack>
              <CardFooter>
                <span>Started {row.started}</span>
                <span>Ends {formatEnds(row.endYear)}</span>
              </CardFooter>
            </Card>
          ))}
        </CardList>
        <MobileSummary>{summaryLabel}</MobileSummary>
      </Section>
    );
  }

  const renderRow = (row: LiabilityRow) => (
    <BodyRow key={row.id} onClick={() => onSelect(row.id)}>
      <Cell $align="left">
        <NameText>{row.name}</NameText>
        <TypeRow>
          <Dot $color={row.typeColor} />
          <TypeLabel>{row.typeLabel}</TypeLabel>
        </TypeRow>
      </Cell>
      <Cell $align="left">
        <RateText>{formatPercent(row.rate, 3)}</RateText>
      </Cell>
      <Cell $align="left">
        <MutedText>{row.started}</MutedText>
      </Cell>
      <Cell $align="right">
        <ValueText>{formatCurrency(row.debt)}</ValueText>
      </Cell>
      <Cell $align="right">
        <MutedText>{formatCurrency(row.paid)}</MutedText>
      </Cell>
      <Cell $align="left">
        <ProgressWrap>
          <ProgressPercent>{formatPercent(row.progress, 1)}</ProgressPercent>
          <ProgressTrack>
            <ProgressFill $width={Math.min(row.progress, 100)} />
          </ProgressTrack>
        </ProgressWrap>
      </Cell>
      <Cell $align="right">
        <EndsText>{formatEnds(row.endYear)}</EndsText>
      </Cell>
    </BodyRow>
  );

  return (
    <Section>
      <TableCard>
        <TableScroll>
          <Table>
            <thead>
              <HeaderRow>
                <HeaderCell $align="left">Liability</HeaderCell>
                <SortableColumnHeader
                  column="rate"
                  label="Rate"
                  align="start"
                  sort={sort}
                  onSort={handleSort}
                />
                <SortableColumnHeader
                  column="started"
                  label="Started"
                  align="start"
                  sort={sort}
                  onSort={handleSort}
                />
                {NUMERIC_COLUMNS.map((column) => (
                  <SortableColumnHeader
                    key={column.key}
                    column={column.key}
                    label={column.label}
                    align="end"
                    sort={sort}
                    onSort={handleSort}
                  />
                ))}
                <SortableColumnHeader
                  column="progress"
                  label="Progress"
                  align="start"
                  sort={sort}
                  onSort={handleSort}
                />
                <SortableColumnHeader
                  column="ends"
                  label="Ends"
                  align="end"
                  sort={sort}
                  onSort={handleSort}
                />
              </HeaderRow>
            </thead>
            <tbody>{rows.map(renderRow)}</tbody>
          </Table>
        </TableScroll>
        <SummaryFooter>{summaryLabel}</SummaryFooter>
      </TableCard>
    </Section>
  );
};
